import React, { useMemo } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { ApiKeys, WorkOsWidgets } from '@workos-inc/widgets';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { Loading } from '@cotera/client/app/components';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { config } from '@cotera/client/config/config';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

const CopyableCodeBlock: React.FC<{ value: string }> = ({ value }) => (
  <div className="relative rounded-lg border border-border bg-muted/30">
    <pre className="overflow-x-auto p-3 pr-12 text-xs font-mono text-foreground whitespace-pre-wrap break-all">
      {value}
    </pre>
    <Button
      variant="ghost"
      size="icon-sm"
      className="absolute right-2 top-2 h-7 w-7 text-muted-foreground hover:text-foreground"
      title="Copy"
      onClick={() => navigator.clipboard.writeText(value)}
    >
      <Icon icon="copy" />
    </Button>
  </div>
);

export const ApiKeysView: React.FC = () => {
  const { getAccessToken, isLoading, user } = useAuth();

  const exampleRequest = useMemo(
    () =>
      [
        `curl "${config.apiUrl}/api/v1/..." \\`,
        `  -H "Authorization: Bearer YOUR_API_KEY"`,
      ].join('\n'),
    []
  );

  if (isLoading) {
    return <Loading.Shimmer className="h-32 w-full" />;
  }

  if (!user) {
    return <div className="text-muted-foreground">Please sign in</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <section className="space-y-2">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <KeyRound className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium">API Keys</h3>
            <p className="text-sm text-muted-foreground">
              Create personal API keys for programmatic access to Cotera public
              API endpoints. Keys are tied to your user account and inherit your
              organization access.
            </p>
          </div>
        </div>
      </section>

      <Alert variant="warning">
        <ShieldAlert />
        <AlertTitle>Keep your keys secret</AlertTitle>
        <AlertDescription>
          API keys grant access to your account. Store them in a secrets manager
          and never commit them to source control.
        </AlertDescription>
      </Alert>

      <section className="space-y-3">
        <div>
          <h4 className="text-sm font-medium">Your keys</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Create, rotate, and revoke keys below. New keys are shown once at
            creation time.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-white p-4">
          <WorkOsWidgets>
            <ApiKeys authToken={getAccessToken} scope="user" />
          </WorkOsWidgets>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h4 className="text-sm font-medium">Usage</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Pass your API key as a Bearer token. Org context is resolved from
            the key&apos;s organization automatically.
          </p>
        </div>
        <CopyableCodeBlock value={exampleRequest} />
      </section>
    </div>
  );
};
